import {
	Box,
	Button,
	Inline,
	Table,
	Stack,
	Container,
	Heading,
	Text,
	Icons,
} from "@sembark-travel/ui/base"
import { useXHR } from "@sembark-travel/xhr"
import {
	Form,
	FileInputField,
	withServerErrors,
	SubmissionError,
	validateFormValues,
	SelectField,
	GetFieldValue,
	TextInputField,
} from "@sembark-travel/ui/form"
import * as Validator from "yup"
import config from "../../config"
import {
	SelectTenantCurrencyInputField,
	useFunctionalCurrencyOfTenant,
} from "../../Currencies"
import { useRef } from "react"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui/copy-to-clipboard"
import { TSheetFolder } from "../../DataDrive"
import { SelectFolder } from "../List"

interface IFormData {
	timezone_offset: number
	metric_sheet_file_name: string
	metric_sheet_folder: TSheetFolder | undefined
	currency: string
	file: File | null
}

const THIS_YEAR = new Date().getFullYear()

const validationSchema = validateFormValues(
	Validator.object().shape({
		timezone_offset: Validator.string().required(),
		metric_sheet_folder: Validator.object().required("Folder is required"),
		metric_sheet_file_name: Validator.string().required(
			"File name is required"
		),
		currency: Validator.string().required(
			"Please select a currency for price input."
		),
	})
)
export function UploadCabMetricPricesViaCSVForm({
	onSuccess,
	onCancel,
	folderDetails,
}: {
	onSuccess: () => void
	onCancel?: () => void
	folderDetails?: TSheetFolder
}) {
	const xhr = useXHR()
	const functionalCurrency = useFunctionalCurrencyOfTenant()
	const initialValues: IFormData = useRef({
		timezone_offset: config.timezoneOffset,
		metric_sheet_folder: folderDetails,
		metric_sheet_file_name: "",
		currency: functionalCurrency.currency,
		file: null,
	}).current
	return (
		<>
			<Form<IFormData>
				initialValues={initialValues}
				validate={validationSchema}
				onSubmit={withServerErrors(async (values) => {
					const data = new FormData()

					if (!values.file) {
						throw Error("Please select a file to upload!")
					}
					data.set("timezone_offset", values.timezone_offset.toString())
					data.set("file", values.file)
					data.set("currency", values.currency)
					if (!values.metric_sheet_folder) {
						throw Error("Please select a folder to upload to!")
					}
					data.set("folder", String(values.metric_sheet_folder.id))
					data.set("file_name", values.metric_sheet_file_name)

					await xhr.post(
						"/data-drive/cab-price-calculation-metric-prices/import-csv",
						data,
						{
							headers: {
								"Content-Type": "multipart/form-data",
							},
						}
					)
					onSuccess()
				})}
				subscription={{ submitting: true, submitErrors: true }}
			>
				{({ submitting, handleSubmit, submitErrors }) => (
					<form
						noValidate
						encType="multipart/form-data"
						onSubmit={handleSubmit}
					>
						<Container paddingY="8" bgColor="default">
							<Stack gap="4">
								<SubmissionError />
								{submitErrors ? (
									<Stack gap="4">
										{submitErrors ? (
											<Box as="ul" listStyleType="disc">
												{Object.keys(submitErrors).map((field) => (
													<Box as="li" key={field}>
														<Text>
															{
																(submitErrors as { [field: string]: string })[
																	field
																]
															}
														</Text>
													</Box>
												))}
											</Box>
										) : null}
										<Text>
											Please verify your CSV File format with the format given
											below. Or contact our support if you need any assistant.
										</Text>
									</Stack>
								) : null}

								<Stack gap="4" maxWidth={"xs"}>
									<SelectField
										select={SelectFolder}
										name="metric_sheet_folder"
										label="Enter a folder name"
										placeholder="eg. Rajasthan"
										creatable
										required
									/>

									<TextInputField
										name="metric_sheet_file_name"
										required
										label={"Enter a file Name"}
										placeholder="eg. Rajasthan Cabs Prices Winter"
									/>
								</Stack>

								<Inline gap="4" collapseBelow="sm">
									<SelectTenantCurrencyInputField
										name="currency"
										label="Currency"
										style={{ maxWidth: "150px" }}
									/>
									<Box flex="1">
										<FileInputField
											label="Select a csv file"
											name="file"
											accept=".csv"
											help={
												<GetFieldValue<string> name="currency">
													{({ value: currency }) => (
														<Text>
															All prices will be saved in "{currency}" currency.
														</Text>
													)}
												</GetFieldValue>
											}
										/>
									</Box>
								</Inline>
								<Inline gap="4" flexWrap="wrap">
									<Button type="submit" disabled={submitting} size="lg">
										{submitting
											? "Uploading..."
											: "Upload Transportation Metric Prices CSV"}
									</Button>
									{onCancel ? (
										<Button onClick={onCancel} level="tertiary" size="lg">
											Cancel
										</Button>
									) : null}
								</Inline>
							</Stack>
						</Container>
					</form>
				)}
			</Form>
			<Container paddingY="8">
				<CopyToClipboard<HTMLDivElement>>
					{({ copy, nodeToCopy }) => (
						<Stack gap="4">
							<Heading as="h4">CSV File Format for Cab Prices</Heading>
							<Text maxWidth="4xl">
								When creating your CSV file for cab prices, please follow this
								format. Hover over the column headers for more information.
								Contact our support team if you have any questions.
							</Text>
							<Text>
								Also, please ensure that only the following system metrics (Sys.
								Metric) are used:
								<Text as="span" fontWeight="semibold">
									{" "}
									days, kms, nights, extra_kms, total, adults, adults_into_days,
									children, children_into_days
								</Text>
								. The system metrics are case-sensitive and will not accept any
								additional or altered metrics. Kindly ensure the metric names
								match exactly as listed above.
							</Text>
							<Box ref={nodeToCopy}>
								<Table
									aria-label="CSV file format for cab price calculation metrics"
									responsive
									className="excel-style-table"
								>
									<Box as="thead" whiteSpace="preserve">
										<tr>
											<th rowSpan={3}>
												<abbr title="Name of the Cab Type (e.g., Sedan, SUV)">
													Cab Type
												</abbr>
											</th>
											<th rowSpan={3}>
												<abbr title="Name of the Calculation Metric (e.g., Base Fare, Per KM)">
													Metric Name
												</abbr>
											</th>
											<th rowSpan={3}>
												<abbr title="Name of the System Metric (e.g., days, nights, kms)">
													Sys. Metric
												</abbr>
											</th>
											<th rowSpan={3}>
												<abbr title="Minimum Quantity of the Metric (e.g,, 100)">
													Min Qty/Day
												</abbr>
											</th>

											<th rowSpan={3}>
												<abbr title="Total using Per-Day Minimum of the Metric">
													Total using Per-Day Min.
												</abbr>
											</th>

											<th colSpan={1}>
												<abbr title="Same season sales rates, used during quotation">
													Season 1
												</abbr>
											</th>
											<th colSpan={1}>
												<abbr title="Not adding any suffix (e.g. (Ops)) will use these rates for sales as well as for Bookings">
													Season 2
												</abbr>
											</th>
										</tr>
										<tr>
											<th>
												1 Jul {THIS_YEAR} - 30 Sep {THIS_YEAR}
											</th>
											<th colSpan={1}>
												13 Oct {THIS_YEAR} - 24 Oct {THIS_YEAR}
											</th>
										</tr>
										<tr>
											<th>
												24 Dec {THIS_YEAR} - 4 Jan {THIS_YEAR + 1}
											</th>
											<th>
												5 Jan {THIS_YEAR + 1} - 31 Mar {THIS_YEAR + 1}
											</th>
										</tr>
									</Box>
									<tbody>
										<tr>
											<td>Sedan</td>
											<td>Base Fare</td>
											<td>days</td>
											<td></td>
											<td></td>

											<td>1400</td>
											<td>1100</td>
										</tr>
										<tr>
											<td>Wagon R</td>
											<td>Per KM</td>
											<td>kms</td>
											<td>100</td>
											<td>
												<abbr title="Total using Per-Day Minimum can be 1(true) or 0(false). In case of false it will consider as Total using Overall Minimum ">
													1
												</abbr>
											</td>

											<td>1400</td>
											<td>1100</td>
										</tr>
										<tr>
											<td>Innova/Xylo</td>
											<td>Per Night</td>
											<td>nights</td>
											<td></td>
											<td></td>

											<td>1400</td>
											<td>1100</td>
										</tr>
										<tr>
											<td>Toyota</td>
											<td>Extra Kms</td>
											<td>extra_kms</td>
											<td>100</td>
											<td>0</td>

											<td>1400</td>
											<td>1100</td>
										</tr>
										<tr>
											<td>Audi</td>
											<td>Total</td>
											<td>total</td>
											<td></td>
											<td></td>

											<td>1400</td>
											<td>1100</td>
										</tr>
										<tr>
											<td>BMW</td>
											<td>Adults</td>
											<td>adults</td>
											<td></td>
											<td></td>

											<td>1400</td>
											<td>1100</td>
										</tr>
										<tr>
											<td>Tesla Model 3</td>
											<td>Adults Into Days</td>
											<td>adults_into_days</td>
											<td></td>
											<td></td>

											<td>1400</td>
											<td>1100</td>
										</tr>
										<tr>
											<td>Honda Civic</td>
											<td>Children</td>
											<td>
												children{" "}
												<abbr title="Complementary children age till">{`{{2y}}`}</abbr>
											</td>
											<td></td>
											<td></td>

											<td>1400</td>
											<td>1100</td>
										</tr>
										<tr>
											<td>Ford Mustang</td>
											<td>Children Into Days</td>
											<td>children_into_days {`{{2y}}`}</td>
											<td></td>
											<td></td>

											<td>1400</td>
											<td>1100</td>
										</tr>
									</tbody>
								</Table>
							</Box>
							<Box>
								<CopyToClipboardButton onClick={() => copy()} level="primary">
									<Icons.ClipboardCopy />
									Copy Template Format
								</CopyToClipboardButton>
							</Box>
						</Stack>
					)}
				</CopyToClipboard>
			</Container>
		</>
	)
}
